import React, { memo, useMemo, useState } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Input from "@react/shared/ui/Input"
import Avatar from "./Avatar"
import { usePlanning } from "./PlanningContext"
import { matches } from "../lib/search"
import { formatHours } from "../lib/hours"

const PersonItem = memo(({ user, hours, base }) => {
  const { handlers, canWrite } = usePlanning()
  const ratio = base > 0 ? Math.min(1, hours / base) : 0
  return (
    <li
      draggable={canWrite}
      onDragStart={event => handlers.dragPerson(user, event)}
      onDragEnd={handlers.dragEnd}
      className={clsx(
        "group flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 select-none transition-[transform,border-color,background-color] duration-150",
        canWrite && "cursor-grab active:cursor-grabbing hover:-translate-y-px",
      )}
    >
      <Avatar user={user} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-slate-800 truncate">{user.name}</div>
        {base > 0 && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
              <div className={clsx("h-full rounded-full", hours > base ? "bg-rose-500" : "bg-brand-400")} style={{ width: `${ratio * 100}%` }} />
            </div>
            <span className="text-[10px] text-slate-400 tabular-nums">{formatHours(hours)}</span>
          </div>
        )}
      </div>
      {canWrite && <FontAwesomeIcon icon="grip-vertical" className="text-[10px] text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </li>
  )
})

const PeoplePalette = ({ users, groups, hoursByUser, weeks }) => {
  const [query, setQuery] = useState("")
  const visible = useMemo(() => users.filter(user => matches(query, user.name)), [users, query])

  return (
    <>
      <div className="px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[13px] font-semibold text-slate-900">{I18n.t("project_planning.palette.people")}</h2>
          <span className="text-[11px] font-semibold text-slate-400 tabular-nums">{visible.length}</span>
        </div>
        <Input variant="search" size="sm" leadingIcon="magnifying-glass" placeholder={I18n.t("project_planning.palette.search_people")} value={query} onChange={event => setQuery(event.target.value)} />
      </div>
      <ul className="flex-1 overflow-y-auto p-1.5">
        {groups.map(group => {
          const members = visible.filter(user => (user.group_id ?? null) === (group.id ?? null))
          if (members.length === 0) return null
          return (
            <React.Fragment key={group.id ?? "none"}>
              <li className="px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 truncate">
                {group.name ? I18n.t("project_planning.group.team_of", { name: group.name }) : I18n.t("project_planning.group.none")}
              </li>
              {members.map(user => (
                <PersonItem key={user.id} user={user} hours={hoursByUser.get(user.id) || 0} base={(user.weekly_hours || 0) * weeks} />
              ))}
            </React.Fragment>
          )
        })}
      </ul>
    </>
  )
}

export default PeoplePalette
